import { API_HOST, EXCHANGE_PATH } from './config'
import { setSessionKind } from './i18n'
import { fetchWithTimeout } from './support'
import type { ApiEnvelope, ConnectData, JoinErrorKind, PrejoinData } from './types'

export class JoinError extends Error {
  kind: JoinErrorKind

  constructor(kind: JoinErrorKind, message: string) {
    super(message)
    this.kind = kind
  }
}

// Congested Cairo mobile networks can leave a request hanging indefinitely;
// without a deadline the join screen just sits there with no way forward.
const REQUEST_TIMEOUT_MS = 15_000

async function post<T>(body: Record<string, string>): Promise<T> {
  let response: Response
  try {
    response = await fetchWithTimeout(
      `${API_HOST}${EXCHANGE_PATH}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The exchange endpoint is unauthenticated (the code IS the credential);
        // never send cookies.
        credentials: 'omit',
        cache: 'no-store',
        body: JSON.stringify(body),
      },
      REQUEST_TIMEOUT_MS,
    )
  } catch {
    throw new JoinError('network', 'Could not reach the server')
  }

  let envelope: ApiEnvelope<T> | undefined
  try {
    envelope = (await response.json()) as ApiEnvelope<T>
  } catch {
    /* non-JSON error body — classified by status below */
  }

  if (!response.ok || envelope?.status !== 'success') {
    throw classify(response.status, envelope?.message)
  }
  // Class or meeting copy from here on (ADR 0013); every exchange answer carries the session.
  setSessionKind((envelope.data as { session?: { kind?: unknown } } | null)?.session?.kind)
  return envelope.data
}

function classify(status: number, message?: string): JoinError {
  const text = (message ?? '').toLowerCase()
  if (text.includes('ended')) return new JoinError('ended', message ?? 'This class has ended')
  if (text.includes('not started') || text.includes('early')) {
    return new JoinError('not_started', message ?? 'This class has not started yet')
  }
  // 425 Too Early: a hidden observer (Ghost) reached the room before any teacher
  // opened it — the room (and its recording) is created by the first real
  // participant to connect, never by an observer. Matched on STATUS, not the
  // message: the existing 'not started' text mapping above is the join-window
  // check (a 422 "come back nearer the start time"), a different thing entirely.
  // The classifier falls through to here only because 425 is not one of those
  // codes, so the ordering is safe.
  if (status === 425) {
    return new JoinError('waiting_for_class', message ?? 'Waiting for the class to open')
  }
  if (status === 401 || status === 403 || status === 404 || status === 410 || status === 422) {
    return new JoinError('expired', message ?? 'This link is no longer valid')
  }
  // 503 means the server is deliberately shedding load (circuit open, ~30s
  // cool-off). Classifying it as a generic error invites immediate re-tapping,
  // which on a shared mobile NAT burns the per-IP throttle for every other
  // student behind the same address during the exact window they should wait out.
  if (status === 503 || status === 429) {
    return new JoinError('unavailable', message ?? 'The class is temporarily unavailable')
  }
  return new JoinError('unknown', message ?? 'Something went wrong')
}

export function exchangePrejoin(code: string): Promise<PrejoinData> {
  return post<PrejoinData>({ code, phase: 'prejoin' })
}

export function exchangeConnect(code: string): Promise<ConnectData> {
  return post<ConnectData>({ code, phase: 'connect' })
}

export function exchangeReconnect(reconnectToken: string): Promise<ConnectData> {
  return post<ConnectData>({ reconnect_token: reconnectToken, phase: 'connect' })
}
